import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { prefetchLotusCasinoTab } from '../../lib/lotusCasinoPrefetch'

import {

  LOTUS_CASINO_TABS,

  getLotusTabByCategory,

  getLotusTabPath,

  parseLotusTabIndexFromPath,

  type LotusCasinoTab,

} from '../../constants/lotusCasinoTabs'

import './LotusCasinoHeaderTabs.css'



export type LotusCasinoHeaderTabsProps = {

  preLoginMode?: boolean

  embeddedCategory?: string | null

  embeddedTabIndex?: number | null

  embeddedView?: string | null

  activeTabIndex?: number | null

  onEmbeddedCategoryChange?: (category: string) => void

  onEmbeddedViewChange?: (view: string | null) => void

  onEmbeddedTabChange?: (tabIndex: number) => void

}



const SCROLL_STEP = 120

const TAB_CDN = 'https://dmcegeg7oly4.cloudfront.net/empwr/tabs'

const TAB_IMAGE_FALLBACKS: Record<string, string[]> = {
  'indian-games': [
    '/imgs/casino-tabs/indian_games.webp',
    '/imgs/super-casino-tabs/indian_games.webp',
    `${TAB_CDN}/indian_games.webp`,
  ],
  'sport-games': [
    '/imgs/super-casino-tabs/sport_games.svg',
    `${TAB_CDN}/sportgames.webp`,
    `${TAB_CDN}/sport_games.webp`,
    `${TAB_CDN}/sports_games.webp`,
  ],
}

const TabTabImage: React.FC<{ tabId: string; primary: string; alt: string }> = ({
  tabId,
  primary,
  alt,
}) => {
  const candidates = React.useMemo(() => {
    const extras = TAB_IMAGE_FALLBACKS[tabId] ?? []
    const merged = [...extras]
    if (primary && !merged.includes(primary)) merged.push(primary)
    return merged.length > 0 ? merged : [primary]
  }, [primary, tabId])

  const [index, setIndex] = React.useState(0)
  const [failed, setFailed] = React.useState(false)
  const src = candidates[Math.min(index, candidates.length - 1)] ?? primary

  if (failed || !src) {
    return <span className="lotus-casino-header-tabs__tab-img-fallback" aria-hidden />
  }

  return (
    <img
      className="lotus-casino-header-tabs__tab-img"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => {
        setIndex((current) => {
          if (current + 1 < candidates.length) return current + 1
          setFailed(true)
          return current
        })
      }}
    />
  )
}

const CarouselChevron = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    {direction === 'left' ? (
      <path
        d="M7.5 9.5L4 6L7.5 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M4.5 2.5L8 6L4.5 9.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
)



const LotusCasinoHeaderTabs: React.FC<LotusCasinoHeaderTabsProps> = ({

  preLoginMode = false,

  embeddedCategory,

  embeddedTabIndex,

  embeddedView,

  activeTabIndex,

  onEmbeddedCategoryChange,

  onEmbeddedViewChange,

  onEmbeddedTabChange,

}) => {

  const navigate = useNavigate()

  const location = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()

  const view = preLoginMode ? embeddedView : searchParams.get('view')

  const category = preLoginMode ? embeddedCategory : searchParams.get('category')

  const scrollRef = useRef<HTMLDivElement>(null)

  const queryClient = useQueryClient()

  const [canScrollLeft, setCanScrollLeft] = useState(false)

  const [canScrollRight, setCanScrollRight] = useState(false)



  const routeTabIndex = parseLotusTabIndexFromPath(location.pathname)

  const categoryTabIndex = getLotusTabByCategory(category ?? undefined)?.tabIndex ?? null



  const resolvedActiveIndex = preLoginMode

    ? view === 'favorites' || view === 'providers'

      ? null

      : (embeddedTabIndex ?? activeTabIndex ?? 1)

    : (activeTabIndex ?? routeTabIndex ?? categoryTabIndex)

  useEffect(() => {
    if (resolvedActiveIndex == null) return
    const tabIndices = LOTUS_CASINO_TABS.map((tab) => tab.tabIndex)
    const activePos = tabIndices.indexOf(resolvedActiveIndex)
    if (activePos < 0) return
    ;[activePos - 2, activePos - 1, activePos + 1, activePos + 2]
      .filter((pos) => pos >= 0 && pos < tabIndices.length)
      .forEach((pos) => {
        prefetchLotusCasinoTab(queryClient, tabIndices[pos], Boolean(preLoginMode))
      })
  }, [preLoginMode, queryClient, resolvedActiveIndex])

  const updateArrows = useCallback(() => {

    const el = scrollRef.current

    if (!el) return

    setCanScrollLeft(el.scrollLeft > 1)

    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)

  }, [])



  useEffect(() => {

    updateArrows()

    const el = scrollRef.current

    if (!el) return undefined

    el.addEventListener('scroll', updateArrows, { passive: true })

    window.addEventListener('resize', updateArrows)

    return () => {

      el.removeEventListener('scroll', updateArrows)

      window.removeEventListener('resize', updateArrows)

    }

  }, [updateArrows])



  const scrollBy = (dir: 'left' | 'right') => {

    scrollRef.current?.scrollBy({

      left: dir === 'left' ? -SCROLL_STEP : SCROLL_STEP,

      behavior: 'smooth',

    })

  }



  const isTabActive = (tab: LotusCasinoTab) => tab.tabIndex === resolvedActiveIndex



  const handleTabHover = useCallback(
    (tabIndex: number) => {
      prefetchLotusCasinoTab(queryClient, tabIndex, Boolean(preLoginMode))
    },
    [preLoginMode, queryClient],
  )

  const handleTabClick = (tab: LotusCasinoTab) => {

    if (tab.targetType === 'route') {

      if (preLoginMode) return

      navigate(tab.target)

      return

    }



    if (preLoginMode && onEmbeddedTabChange) {

      onEmbeddedTabChange(tab.tabIndex)

      onEmbeddedViewChange?.(null)

      return

    }



    if (view === 'favorites') {

      const next = new URLSearchParams(searchParams)

      next.delete('view')

      setSearchParams(next)

    }



    navigate(getLotusTabPath(tab.tabIndex))

  }



  return (

    <div

      className={`lotus-casino-header-tabs${canScrollLeft ? '' : ' left-arrow-hidden'}${canScrollRight ? '' : ' right-arrow-hidden'}`}

    >

      {canScrollLeft && (
        <button
          type="button"
          className="lotus-casino-header-tabs__arrow lotus-casino-header-tabs__arrow--left"
          onClick={() => scrollBy('left')}
          aria-label="Scroll tabs left"
        >
          <CarouselChevron direction="left" />
        </button>
      )}

      <div ref={scrollRef} className="lotus-casino-header-tabs__container">

        {LOTUS_CASINO_TABS.map((tab) => {

          const active = isTabActive(tab)

          const href = tab.targetType === 'route' ? tab.target : getLotusTabPath(tab.tabIndex)

          return (

            <a

              key={tab.id}

              href={preLoginMode ? '#' : href}

              className={`lotus-casino-header-tabs__tab tab-item${active ? ' selected' : ''}${active ? ' lower-padding' : ''}`}

              style={{ ['--super-casino-tab-flex' as string]: '1 0 94px' }}

              onClick={(e) => {

                e.preventDefault()

                handleTabClick(tab)

              }}

              onMouseEnter={() => handleTabHover(tab.tabIndex)}

              onFocus={() => handleTabHover(tab.tabIndex)}

            >

              <div className="lotus-casino-header-tabs__icon-container">

                <TabTabImage tabId={tab.id} primary={tab.image} alt={tab.title} />

              </div>

              <div className="lotus-casino-header-tabs__title-container">{tab.title}</div>

            </a>

          )

        })}

      </div>

      {canScrollRight && (
        <button
          type="button"
          className="lotus-casino-header-tabs__arrow lotus-casino-header-tabs__arrow--right"
          onClick={() => scrollBy('right')}
          aria-label="Scroll tabs right"
        >
          <CarouselChevron direction="right" />
        </button>
      )}

    </div>

  )

}



export default LotusCasinoHeaderTabs


